import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { Megaphone, Send, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { apiRequest } from "@/lib/queryClient";
import { redactSensitive } from "@kudoz/shared/redactDiagnostics";

const FEEDBACK_TYPES = [
  { value: "bug", label: "Bug Report" },
  { value: "problem", label: "Problem" },
  { value: "suggestion", label: "Suggestion" },
  { value: "other", label: "Other" },
] as const;

export default function FeedbackWidget() {
  const [location] = useLocation();
  const [open, setOpen] = useState(false);
  const [type, setType] = useState<string>("bug");
  const [message, setMessage] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const { toast } = useToast();
  const { user, isAuthenticated } = useAuth();

  const mutation = useMutation({
    mutationFn: async () => {
      const payload = {
        type,
        message,
        userId: isAuthenticated && user ? String(user.id) : null,
        userEmail: isAuthenticated && user ? user.email : null,
        userName:
          isAuthenticated && user
            ? user.displayName || user.name || null
            : null,
        pageUrl: redactSensitive(window.location.href),
        userAgent: navigator.userAgent,
        screenSize: `${window.screen.width}x${window.screen.height}`,
      };
      return apiRequest("POST", "/api/feedback", payload);
    },
    onSuccess: () => {
      setSubmitted(true);
      setTimeout(() => {
        setOpen(false);
        setSubmitted(false);
        setMessage("");
        setType("bug");
      }, 2000);
    },
    onError: () => {
      toast({
        title: "Something went wrong",
        description: "We couldn't submit your feedback. Please try again.",
        variant: "destructive",
      });
    },
  });

  if (location === "/checkout" || location.startsWith("/checkout/")) {
    return null;
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (message.trim().length < 5) {
      toast({
        title: "Message too short",
        description: "Please describe your feedback in at least 5 characters.",
        variant: "destructive",
      });
      return;
    }
    mutation.mutate();
  };

  const handleOpenChange = (value: boolean) => {
    if (!mutation.isPending) {
      setOpen(value);
      if (!value) {
        setSubmitted(false);
        setMessage("");
        setType("bug");
      }
    }
  };

  return (
    <>
      <div className="fixed bottom-24 right-4 sm:bottom-6 sm:right-6 z-40">
        <Button
          size="icon"
          onClick={() => setOpen(true)}
          className="rounded-full shadow-md"
          aria-label="Send feedback"
          data-testid="button-feedback-widget"
        >
          <Megaphone className="w-5 h-5" />
        </Button>
      </div>

      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent
          className="sm:max-w-sm mx-4 max-h-[90dvh] flex flex-col"
          data-testid="modal-feedback"
        >
          <DialogHeader className="flex-shrink-0">
            <DialogTitle>Send Feedback</DialogTitle>
          </DialogHeader>

          {submitted ? (
            <div className="flex flex-col items-center justify-center py-8 gap-3 text-center">
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                <Send className="w-5 h-5 text-primary" />
              </div>
              <p className="font-medium">Thanks for your feedback!</p>
              <p className="text-sm text-muted-foreground">
                We'll look into it shortly.
              </p>
            </div>
          ) : (
            <form
              onSubmit={handleSubmit}
              className="flex flex-col gap-4 overflow-y-auto flex-1 pr-1"
            >
              <div>
                <Label htmlFor="feedback-type">Type</Label>
                <Select value={type} onValueChange={setType}>
                  <SelectTrigger
                    id="feedback-type"
                    data-testid="select-feedback-type"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {FEEDBACK_TYPES.map((t) => (
                      <SelectItem key={t.value} value={t.value}>
                        {t.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex flex-col gap-1.5">
                <Label htmlFor="feedback-message">Message</Label>
                <Textarea
                  id="feedback-message"
                  placeholder="Describe the issue or suggestion…"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  rows={4}
                  data-testid="textarea-feedback-message"
                />
                <p className="text-xs text-muted-foreground">
                  We'll automatically capture the current page and your browser
                  info to help us debug.
                </p>
              </div>

              <div className="flex justify-end gap-2 pb-1">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => handleOpenChange(false)}
                  disabled={mutation.isPending}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={mutation.isPending || message.trim().length < 5}
                  data-testid="button-feedback-submit"
                >
                  {mutation.isPending ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Sending…
                    </>
                  ) : (
                    "Send Feedback"
                  )}
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
